import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from "react";

const AuthContext = createContext();

// Bumped whenever the shape written by login() changes. A stored blob with an
// older/absent __v is discarded so the user re-authenticates instead of running
// on a half-populated auth object.
const AUTH_VERSION = 2;

const LOGGED_OUT = { isAuthenticated: false, userID: null };

const persistAuth = (authData) =>
  localStorage.setItem("auth", JSON.stringify(authData));

const readStoredAuth = () => {
  const storedAuth = localStorage.getItem("auth");
  if (!storedAuth) return LOGGED_OUT;

  try {
    const parsed = JSON.parse(storedAuth);
    // Sessions written before AUTH_VERSION 2 can be missing permissions
    // entirely, which silently collapses the sidebar to its ungated items.
    if (
      parsed.isAuthenticated &&
      (parsed.__v !== AUTH_VERSION || !Array.isArray(parsed.permissions))
    ) {
      localStorage.removeItem("auth");
      return LOGGED_OUT;
    }
    return parsed;
  } catch (error) {
    console.error("Discarding unreadable stored auth:", error);
    localStorage.removeItem("auth");
    return LOGGED_OUT;
  }
};

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(readStoredAuth);
  const [Sources, setSources] = useState([]);
  const count = useRef(0);

  useEffect(() => {
    if (count.current === 0) {
      fetchSource();
      count.current += 1;
    }
  }, []);

  // Maps the raw /login/ user payload onto the auth shape. Only call this with
  // the API response — to change a field on an existing session use updateAuth.
  const login = useCallback((userData) => {
    const authData = {
      __v: AUTH_VERSION,
      isAuthenticated: true,
      userID: userData.id,
      username: userData.username,
      email: userData.email,
      role_name: userData.role_name,
      role_id: userData.role_id,
      qc_columns: userData.qc_columns,
      id: userData.id,
      // The API sends role_permission; fall back to permissions so an
      // already-mapped auth object passed in here can't strip the menu.
      permissions: userData.role_permission ?? userData.permissions ?? [],
    };
    setAuth(authData);
    persistAuth(authData);
  }, []);

  // Merge a partial update into the current session and persist it, leaving
  // every other field (notably permissions) untouched.
  const updateAuth = useCallback((patch) => {
    setAuth((prevAuth) => {
      const nextAuth = { ...prevAuth, ...patch };
      persistAuth(nextAuth);
      return nextAuth;
    });
  }, []);

  const logout = useCallback(() => {
    setAuth(LOGGED_OUT);
    localStorage.removeItem("auth");
  }, []);

  const fetchSource = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/get_sources?user_id=${auth.userID}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        setSources([]);
        throw new Error("Network response was not ok");
      }

      const data = await response.json();
      const source = data.sources.map((item) => ({
        label: item,
        value: item,
      }));

      setSources(source);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const value = {
    auth,
    login,
    updateAuth,
    logout,
    Sources,
    fetchSource
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
